import cn from 'classnames';
import React, { useEffect, useRef } from 'react';

import IconChevronUp from 'editor/src/component/Icon/IconChevronUp';

import styles from './index.module.scss';

interface Props {
  children: React.ReactNode;
  header: React.ReactNode;
  setCollapsed: (collapsed: boolean) => void;
  collapsed: boolean;
  className?: string;
  headerClassName?: string;
  subheader?: React.ReactNode;
  subheaderClassName?: string;
  onTransitionEnd?: () => void;
}

// Keep in sync with the max-height transitions in index.module.scss.
const EXPAND_DURATION_MS = 300;

function Accordion({
  header,
  headerClassName,
  children,
  collapsed,
  setCollapsed,
  className,
  subheader,
  subheaderClassName,
  onTransitionEnd,
}: Props) {
  function toggleCollapsed() {
    setCollapsed(!collapsed);
  }

  const contentRef = useRef<HTMLDivElement>(null);
  const animateNextChange = useRef(false);

  // The collapse animates a measured max-height, and the cap is dropped once the
  // animation is over. A hardcoded cap (this used to be 2000px) silently clips
  // anything taller than it, and a `1fr` grid row would instead stretch to the
  // space the parent happens to offer - neither can show a long list in full.
  useEffect(() => {
    const content = contentRef.current;
    if (!content) {
      return undefined;
    }

    if (!animateNextChange.current) {
      animateNextChange.current = true;
      content.style.maxHeight = collapsed ? '0px' : 'none';
      return undefined;
    }

    if (collapsed) {
      content.style.maxHeight = `${content.scrollHeight}px`;
      // Force a reflow so the transition has a start value to animate from.
      // (A requestAnimationFrame would never fire in a background tab, which
      // would leave the section stuck open.)
      void content.offsetHeight;
      content.style.maxHeight = '0px';
      return undefined;
    }

    content.style.maxHeight = `${content.scrollHeight}px`;
    const timer = window.setTimeout(() => {
      content.style.maxHeight = 'none';
    }, EXPAND_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [collapsed]);

  return (
    <>
      <div
        onTransitionEnd={onTransitionEnd}
        className={cn(styles.Accordion, className, {
          [styles.collapsed]: collapsed,
        })}
        onClick={toggleCollapsed}
      >
        <div className={cn(styles.header, headerClassName)}>
          {header}
          <div
            className={cn(styles.buttonToggle, {
              'cy-accordion-collapse-btn': !collapsed,
              'cy-accordion-expand-btn': collapsed,
            })}
          >
            <IconChevronUp />
          </div>
        </div>
        {subheader && <div className={cn(styles.subheader, 'pb-2', subheaderClassName)}>{subheader}</div>}
      </div>
      <div ref={contentRef} className={cn(styles.content, { [styles.collapsed]: collapsed })}>
        {children}
        <div className="pt-2" />
      </div>
    </>
  );
}

export default Accordion;
